import { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  RadioGroup,
  Radio,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

import { UserSearchDropdown, SelectedUserDisplay, type User } from './UserComponents';
import { AccessResponse, Permission } from '@/types/permissions';

const useStyles = makeStyles({
  dialogSurface: {
    // Compact, focused mini-form. 520px keeps the dialog from feeling
    // heavyweight while still giving the Role radio list room to breathe.
    width: '520px',
    maxWidth: '90vw',
  },
  dialogSection: {
    marginBottom: tokens.spacingVerticalL,
  },
  dialogSectionTitle: {
    marginBottom: tokens.spacingVerticalS,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    // Small gap reads as a group without the visual weight of hard dividers.
    gap: tokens.spacingVerticalXXS,
  },
  radioItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    // Comfortable hit area + concentric radius so the hover background
    // feels like a button rather than a stripe across the dialog.
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    // Interruptible — specify exact properties (never transition: all).
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  radioDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
  },
  subtleText: {
    color: tokens.colorNeutralForeground3,
  },
  errorMessageBar: {
    marginBottom: tokens.spacingVerticalS,
  },
});

export interface RoleAssignmentDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** The dialog title */
  title?: string;
  /** The submit button label */
  submitLabel?: string;
  /** The mount node for the dialog (should be the parent dialog/modal element) */
  mountNode?: HTMLElement | null | undefined;
  /** List of supported permissions that can be assigned */
  supportedPermissions: Permission[];
  /** Function to check access for a specific user by ID */
  checkAccess: (userId: string) => Promise<AccessResponse>;
  /** Callback when assignment is submitted */
  onSubmit: (user: User, permissions: string[]) => Promise<void>;
  /** Initial user to pre-populate (for edit mode) */
  initialUser?: User | null;
};

/**
 * RoleAssignmentDialog - A centered modal dialog for assigning permissions to a user
 * Supports customizable title and submit button label
 */
export function RoleAssignmentDialog({
  open,
  onClose,
  title = 'Add role assignment',
  submitLabel = 'Add',
  mountNode,
  supportedPermissions,
  checkAccess,
  onSubmit,
  initialUser,
}: RoleAssignmentDialogProps): JSX.Element {
  const styles = useStyles();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<string | null>(null);
  const [initialPermission, setInitialPermission] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleUserSelect = useCallback(async (user: User) => {
    setSelectedUser(user);
    const access = await checkAccess(user.id);
    const permissionId = access.permissions[0]?.id ?? null;
    setSelectedPermission(permissionId);
    setInitialPermission(permissionId);
  }, [checkAccess]);

  useEffect(() => {
    if (!open) return;
    // Reset every time the dialog opens (not on close) so the exit
    // animation doesn't briefly flash the "search user" state before
    // the surface unmounts. In edit mode, pre-populate from initialUser.
    setSubmitError(null);
    if (initialUser) {
      handleUserSelect(initialUser);
    } else {
      setSelectedUser(null);
      setSelectedPermission(null);
      setInitialPermission(null);
    }
    // Intentionally narrow to `initialUser?.id`: depending on the object
    // reference would re-fire `handleUserSelect` (and its `checkAccess`
    // network call) whenever the parent recreates the User on a refetch,
    // even when the underlying user is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUser?.id, handleUserSelect]);

  const handlePermissionChange = (permissionId: string) => {
    setSelectedPermission(permissionId);
    setSubmitError(null);
  };

  const hasPermissionChanges = selectedUser !== null && selectedPermission !== initialPermission;

  const handleClose = () => {
    if (isSubmitting) return;
    // Do NOT clear local state here; the close animation would briefly
    // re-render the dialog in its empty "Add user" state. The next
    // open-effect resets everything fresh.
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedUser || !selectedPermission) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(selectedUser, [selectedPermission]);
      // Same reasoning as handleClose: let the next open reset state.
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to update permissions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, { open: isOpen }) => !isOpen && handleClose()}
    >
      <DialogSurface
        className={styles.dialogSurface}
        {...(mountNode !== undefined ? { mountNode } : {})}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={isSubmitting}
              />
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            {/*
              Persistent live region for submission errors. The container always
              exists in the DOM so screen readers can announce the message when
              submitError is set — a freshly-mounted role=alert node does not get
              announced reliably (WCAG 4.1.3 Status Messages).
            */}
            <div role="alert" aria-live="assertive">
              {submitError && (
                <MessageBar intent="error" className={styles.errorMessageBar}>
                  <MessageBarBody>{submitError}</MessageBarBody>
                </MessageBar>
              )}
            </div>
            {/* User Selection */}
            <div className={styles.dialogSection}>
              <Text weight="semibold" className={styles.dialogSectionTitle} block>
                Select user
              </Text>
              {!selectedUser ? (
                <UserSearchDropdown
                  onUserSelect={handleUserSelect}
                  placeholder="Search for a user to assign..."
                  ariaLabel="Search user to assign"
                />
              ) : (
                /*
                 * No `onClear` is passed, so the X is hidden in both
                 * add and edit modes. To choose a different user, the
                 * user cancels and reopens the dialog.
                 */
                <SelectedUserDisplay user={selectedUser} />
              )}
            </div>

            {/* Permissions Selection */}
            {selectedUser && (
              <div className={styles.dialogSection}>
                <Text id="role-selection-label" weight="semibold" className={styles.dialogSectionTitle} block>
                  Role
                </Text>
                <RadioGroup
                  className={styles.radioGroup}
                  value={selectedPermission ?? ''}
                  onChange={(_, data) => handlePermissionChange(data.value)}
                  disabled={isSubmitting}
                  // The visible "Role" heading is a sibling <Text>, not a Field
                  // label, so wire it explicitly for AT (WCAG 4.1.2 Name, Role,
                  // Value and 1.3.1 Info and Relationships).
                  aria-labelledby="role-selection-label"
                >
                  {supportedPermissions.map((permission) => (
                    /*
                      Use a <label> element so a click anywhere in the
                      row (name + description) selects the Radio via
                      implicit form-control association. The Radio
                      keeps its `aria-labelledby` / `aria-describedby`
                      wiring so AT still announces a single name +
                      description on focus.
                    */
                    <label key={permission.id} className={styles.radioItem}>
                      <Radio
                        value={permission.id}
                        aria-labelledby={`role-name-${permission.id}`}
                        aria-describedby={`role-desc-${permission.id}`}
                      />
                      <div className={styles.radioDetails}>
                        <Text id={`role-name-${permission.id}`} weight="medium">{permission.name}</Text>
                        <Text id={`role-desc-${permission.id}`} size={200} className={styles.subtleText}>
                          {permission.description}
                        </Text>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!hasPermissionChanges || isSubmitting}
              onClick={handleSubmit}
              {...(isSubmitting ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {isSubmitting ? 'Saving…' : submitLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

RoleAssignmentDialog.displayName = 'RoleAssignmentDialog';
